import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { contractsApi, ContractType, CreateContractPayload } from '../../api/contractsApi';
import { apartmentsApi, Apartment } from '../../api/apartmentsApi';
import { feeCategoriesApi, FeeCategory } from '../../api/feeCategoriesApi';
import ApartmentMultiSelect from '../../components/apartments/ApartmentMultiSelect';
import ContractAmountFields from '../../components/contracts/ContractAmountFields';
import ContractFileField, { PendingContractFile } from '../../components/contracts/ContractFileField';
import { ContractFile } from '../../api/contractsApi';
import { formatDateToDisplay, parseDisplayDateToIso } from '../../utils/apartmentDate';
import { isCasualParkingFeeCode } from '../../utils/feeCategoryCodeGrouping';
import { formatContractAmountField, parseContractAmountField } from '../../utils/contractAmount.util';
import { formatVndInput } from '../../utils/vndFormat';
import { saveContractWithPendingFiles } from '../../utils/contractSave';

interface ContractFormModalProps {
  isOpen: boolean;
  contractType: ContractType;
  buildingId: string;
  contractId?: string | null;
  onClose: () => void;
  onSaved: () => void;
}

const emptyForm = () => ({
  code: '',
  name: '',
  notes: '',
  customerName: '',
  vendorName: '',
  feeCategoryId: '',
  valueBeforeVat: '',
  valueWithVat: '',
  totalDiscount: '',
  totalVat: '',
  vatPercent: '10',
  startDate: '',
  endDate: '',
  liquidatedDate: '',
  contractTerm: '',
  signedDate: '',
  status: 'PENDING_SIGNATURE',
  contractKind: '',
  templateCode: '',
  approvalWorkflow: '',
  isVerified: false,
  parentContractCode: '',
  paymentCycleMonths: '1',
  collectionTiming: 'PREPAID' as 'PREPAID' | 'POSTPAID',
  priorCollectedAmount: '',
  lastRecordedBillingPeriod: '',
  apartmentIds: [] as string[],
});

const ContractFormModal: React.FC<ContractFormModalProps> = ({
  isOpen,
  contractType,
  buildingId,
  contractId,
  onClose,
  onSaved,
}) => {
  const isEdit = Boolean(contractId);
  const isRevenue = contractType === 'REVENUE';
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [feeCategories, setFeeCategories] = useState<FeeCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [existingFiles, setExistingFiles] = useState<ContractFile[]>([]);
  const [pendingFiles, setPendingFiles] = useState<PendingContractFile[]>([]);
  const [deletingFileIds, setDeletingFileIds] = useState<string[]>([]);
  const [invoiceTracking, setInvoiceTracking] = useState<{
    collected: number;
    invoiceCollected: number;
    lastCollectionPeriod: string | null;
    usesManualInvoiceSchedule: boolean;
  } | null>(null);
  useEffect(() => {
    if (!isOpen || !buildingId) return;
    loadData();
  }, [isOpen, buildingId, contractId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [apartmentList, feeList] = await Promise.all([
        apartmentsApi.getApartments({ buildingId }),
        feeCategoriesApi.getAll(buildingId),
      ]);
      setApartments(apartmentList);
      const prefix = isRevenue ? 'T-KTDV' : 'C-CP';
      setFeeCategories(
        feeList.filter(
          (fc) =>
            fc.feeCode?.startsWith(prefix) ||
            fc.feeCode === 'KTDV' ||
            isCasualParkingFeeCode(fc.feeCode) ||
            fc.feeCode?.startsWith('C-CPBT'),
        ),
      );

      if (contractId) {
        const contract = await contractsApi.getById(contractId);
        setExistingFiles(contract.files || []);
        setPendingFiles([]);
        setDeletingFileIds([]);
        setInvoiceTracking({
          collected: contract.collected ?? 0,
          invoiceCollected: contract.invoiceCollected ?? 0,
          lastCollectionPeriod: contract.lastCollectionPeriod ?? null,
          usesManualInvoiceSchedule: contract.usesManualInvoiceSchedule ?? false,
        });
        setForm({
          code: contract.code,
          name: contract.name,
          notes: contract.notes || '',
          customerName: contract.customerName || '',
          vendorName: contract.vendorName || '',
          feeCategoryId: contract.feeCategoryId || '',
          valueBeforeVat: formatContractAmountField(contract.valueBeforeVat),
          valueWithVat: formatContractAmountField(contract.valueWithVat),
          totalDiscount: formatContractAmountField(contract.totalDiscount),
          totalVat: formatContractAmountField(contract.totalVat),
          vatPercent: String(contract.vatPercent),
          startDate: formatDateToDisplay(contract.startDate),
          endDate: formatDateToDisplay(contract.endDate),
          liquidatedDate: formatDateToDisplay(contract.liquidatedDate),
          contractTerm: contract.contractTerm || '',
          signedDate: formatDateToDisplay(contract.signedDate),
          status: contract.status,
          contractKind: contract.contractKind || '',
          templateCode: contract.templateCode || '',
          approvalWorkflow: contract.approvalWorkflow || '',
          isVerified: contract.isVerified,
          parentContractCode: contract.parentContractCode || '',
          paymentCycleMonths: String(contract.paymentCycleMonths),
          collectionTiming: contract.collectionTiming ?? 'PREPAID',
          priorCollectedAmount: formatContractAmountField(contract.priorCollectedAmount ?? 0),
          lastRecordedBillingPeriod: contract.lastRecordedBillingPeriod || '',
          apartmentIds: contract.apartments?.map((a) => a.apartment.id) || [],
        });
      } else {
        setInvoiceTracking(null);
        setForm(emptyForm());
        setExistingFiles([]);
        setPendingFiles([]);
        setDeletingFileIds([]);
      }
    } catch {
      toast.error('Không thể tải dữ liệu hợp đồng');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: string | boolean | string[]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const startIso = parseDisplayDateToIso(form.startDate);
    if (!buildingId || !form.code.trim() || !form.name.trim() || !startIso) {
      toast.error('Vui lòng điền Mã HĐ, Tên HĐ và Ngày bắt đầu (dd/mm/yyyy)');
      return;
    }

    const payload: CreateContractPayload = {
      buildingId,
      contractType,
      code: form.code.trim(),
      name: form.name.trim(),
      notes: form.notes || undefined,
      customerName: isRevenue ? form.customerName || undefined : undefined,
      vendorName: !isRevenue ? form.vendorName || undefined : undefined,
      feeCategoryId: form.feeCategoryId || undefined,
      valueBeforeVat: parseContractAmountField(form.valueBeforeVat),
      valueWithVat: parseContractAmountField(form.valueWithVat),
      totalDiscount: parseContractAmountField(form.totalDiscount),
      totalVat: parseContractAmountField(form.totalVat),
      vatPercent: Number(form.vatPercent) || 0,
      startDate: startIso,
      endDate: parseDisplayDateToIso(form.endDate),
      liquidatedDate: parseDisplayDateToIso(form.liquidatedDate),
      contractTerm: form.contractTerm || undefined,
      signedDate: parseDisplayDateToIso(form.signedDate),
      status: form.status as CreateContractPayload['status'],
      contractKind: form.contractKind || undefined,
      templateCode: form.templateCode || undefined,
      approvalWorkflow: form.approvalWorkflow || undefined,
      isVerified: form.isVerified,
      parentContractCode: form.parentContractCode || undefined,
      paymentCycleMonths: Number(form.paymentCycleMonths) || 1,
      collectionTiming: isRevenue ? form.collectionTiming : undefined,
      priorCollectedAmount: isRevenue ? parseContractAmountField(form.priorCollectedAmount) : undefined,
      lastRecordedBillingPeriod: isRevenue && form.lastRecordedBillingPeriod.trim()
        ? form.lastRecordedBillingPeriod.trim()
        : undefined,
      apartmentIds: form.apartmentIds,
    };

    setSaving(true);
    try {
      const result = await saveContractWithPendingFiles({
        isEdit: Boolean(isEdit && contractId),
        contractId,
        payload,
        pendingFiles,
      });
      if (result === 'contract_failed') {
        return;
      }
      if (result === 'saved') {
        toast.success(isEdit ? 'Đã cập nhật hợp đồng' : 'Đã tạo hợp đồng');
      }
      onSaved();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 overflow-y-auto py-8 px-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-xl font-bold">
            {isEdit ? 'Sửa' : 'Thêm'} {isRevenue ? 'hợp đồng thu' : 'hợp đồng chi'}
          </h2>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl leading-none">
            ×
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-500">Đang tải...</div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Mã hợp đồng *</label>
                <input className="w-full border rounded px-3 py-2" value={form.code} onChange={(e) => handleChange('code', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Tên hợp đồng *</label>
                <input className="w-full border rounded px-3 py-2" value={form.name} onChange={(e) => handleChange('name', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Mã hợp đồng gốc</label>
                <input className="w-full border rounded px-3 py-2" value={form.parentContractCode} onChange={(e) => handleChange('parentContractCode', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Mã danh mục phí</label>
                <select className="w-full border rounded px-3 py-2" value={form.feeCategoryId} onChange={(e) => handleChange('feeCategoryId', e.target.value)}>
                  <option value="">— Chọn —</option>
                  {feeCategories.map((fc) => (
                    <option key={fc.id} value={fc.id}>{fc.feeCode} — {fc.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{isRevenue ? 'Tên khách hàng' : 'Nhà cung cấp'}</label>
                <input
                  className="w-full border rounded px-3 py-2"
                  value={isRevenue ? form.customerName : form.vendorName}
                  onChange={(e) => handleChange(isRevenue ? 'customerName' : 'vendorName', e.target.value)}
                />
              </div>
              <ContractAmountFields
                values={{
                  valueBeforeVat: form.valueBeforeVat,
                  valueWithVat: form.valueWithVat,
                  vatPercent: form.vatPercent,
                  totalDiscount: form.totalDiscount,
                  totalVat: form.totalVat,
                }}
                onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
              />
              <ContractFileField
                existingFiles={existingFiles}
                pendingFiles={pendingFiles}
                onPendingFilesChange={setPendingFiles}
                onDeleteExisting={contractId ? async (fileId) => {
                  setDeletingFileIds((prev) => [...prev, fileId]);
                  try {
                    await contractsApi.removeContractFile(contractId, fileId);
                    setExistingFiles((prev) => prev.filter((f) => f.id !== fileId));
                    toast.success('Đã xóa file');
                  } catch {
                    toast.error('Không thể xóa file');
                  } finally {
                    setDeletingFileIds((prev) => prev.filter((id) => id !== fileId));
                  }
                } : undefined}
                deletingFileIds={deletingFileIds}
              />
              <div>
                <label className="block text-sm font-medium mb-1">Ngày bắt đầu *</label>
                <input placeholder="dd/mm/yyyy" className="w-full border rounded px-3 py-2" value={form.startDate} onChange={(e) => handleChange('startDate', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Ngày kết thúc</label>
                <input placeholder="dd/mm/yyyy" className="w-full border rounded px-3 py-2" value={form.endDate} onChange={(e) => handleChange('endDate', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Ngày thanh lý</label>
                <input placeholder="dd/mm/yyyy" className="w-full border rounded px-3 py-2" value={form.liquidatedDate} onChange={(e) => handleChange('liquidatedDate', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Ngày ký</label>
                <input placeholder="dd/mm/yyyy" className="w-full border rounded px-3 py-2" value={form.signedDate} onChange={(e) => handleChange('signedDate', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Kỳ thanh toán (Tháng)</label>
                <input type="number" min={1} className="w-full border rounded px-3 py-2" value={form.paymentCycleMonths} onChange={(e) => handleChange('paymentCycleMonths', e.target.value)} />
              </div>
              {isRevenue ? (
                <div>
                  <label className="block text-sm font-medium mb-1">Hình thức thu</label>
                  <select
                    className="w-full border rounded px-3 py-2"
                    value={form.collectionTiming}
                    onChange={(e) => handleChange('collectionTiming', e.target.value)}
                  >
                    <option value="PREPAID">Thu trước (kỳ = tháng bắt đầu HĐ)</option>
                    <option value="POSTPAID">Thu sau (kỳ = cuối chu kỳ đầu)</option>
                  </select>
                </div>
              ) : null}
              {isRevenue ? (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1">Số tiền đã thu (trước hệ thống)</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="w-full border rounded px-3 py-2"
                      value={form.priorCollectedAmount}
                      onChange={(e) =>
                        handleChange('priorCollectedAmount', formatVndInput(e.target.value))
                      }
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Không tạo phiếu thu — dùng cùng kỳ đã thu cuối và Tạo HĐ để ghi nhận hóa đơn Đã thanh toán.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Kỳ thu phí đã thu cuối</label>
                    <input
                      placeholder="MM/YYYY"
                      className="w-full border rounded px-3 py-2"
                      value={form.lastRecordedBillingPeriod}
                      onChange={(e) => handleChange('lastRecordedBillingPeriod', e.target.value)}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Dùng khi Tạo HĐ / Tạo HĐ thủ công để ghi Đã thanh toán các kỳ ≤ kỳ này (không phiếu thu).
                    </p>
                  </div>
                  {isEdit && invoiceTracking ? (
                    <div className="md:col-span-2 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                      <p>
                        Đã thu theo hóa đơn:{' '}
                        <span className="font-medium">
                          {formatContractAmountField(invoiceTracking.invoiceCollected)}đ
                        </span>
                        {invoiceTracking.collected !== invoiceTracking.invoiceCollected
                          ? ` · Tổng hiển thị (gồm HT): ${formatContractAmountField(invoiceTracking.collected)}đ`
                          : null}
                      </p>
                      <p className="mt-1">
                        Kỳ thu cuối (theo HĐ):{' '}
                        <span className="font-medium">
                          {invoiceTracking.lastCollectionPeriod || '—'}
                        </span>
                        {invoiceTracking.usesManualInvoiceSchedule
                          ? ' · Lịch thu thủ công'
                          : null}
                      </p>
                    </div>
                  ) : null}
                </>
              ) : null}
              <div>
                <label className="block text-sm font-medium mb-1">Trạng thái</label>
                <select className="w-full border rounded px-3 py-2" value={form.status} onChange={(e) => handleChange('status', e.target.value)}>
                  <option value="PENDING_SIGNATURE">Chờ ký</option>
                  <option value="SIGNED">Đã ký</option>
                  <option value="LIQUIDATED">Đã thanh lý</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Loại hợp đồng</label>
                <input className="w-full border rounded px-3 py-2" value={form.contractKind} onChange={(e) => handleChange('contractKind', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Mẫu hợp đồng</label>
                <input className="w-full border rounded px-3 py-2" value={form.templateCode} onChange={(e) => handleChange('templateCode', e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Ghi chú</label>
                <textarea className="w-full border rounded px-3 py-2" rows={2} value={form.notes} onChange={(e) => handleChange('notes', e.target.value)} />
              </div>
            </div>

            <ApartmentMultiSelect
              apartments={apartments}
              selectedApartmentIds={form.apartmentIds}
              onChange={(ids) => handleChange('apartmentIds', ids)}
            />

            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.isVerified} onChange={(e) => handleChange('isVerified', e.target.checked)} />
              Đã xác thực
            </label>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <button type="button" onClick={onClose} className="px-4 py-2 border rounded">
                Hủy
              </button>
              <button type="submit" disabled={saving} className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50">
                {saving ? 'Đang lưu...' : 'Lưu'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ContractFormModal;
